// Compact row view of a library entry. Used by the simplified Library
// page so users get a dense, scannable list (one row per entry) instead
// of a 3-column card grid. Keeps the same affordances as the card —
// title links to the canonical destination, fork/clone buttons live on
// the right — but trims everything optional (mastery summary, tag chips
// beyond two, action descriptions) to keep each row to a single line on
// desktop and two lines on mobile.
import { Link } from "react-router-dom";
import {
  BookOpen,
  FlaskConical,
  MessageSquareQuote,
  Rocket,
  Users,
  GitFork,
  Eye,
  Star,
  Bot,
} from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { LibraryEntry, LibraryKind } from "@/hooks/useLibrary";
import { bumpLibraryView } from "@/hooks/useLibrary";
import ForkLibraryButton from "@/components/library/ForkLibraryButton";
import ForkToChatButton from "@/components/library/ForkToChatButton";
import CloneArtifactRunDialog from "@/components/agents/CloneArtifactRunDialog";

const KIND_META: Record<
  LibraryKind,
  { label: string; Icon: typeof BookOpen; href: (e: LibraryEntry) => string | null }
> = {
  guide: {
    label: "Guide",
    Icon: BookOpen,
    href: (e) => {
      const slug = (e.payload as { slug?: string }).slug;
      return slug ? `/learn/${slug}` : null;
    },
  },
  research: {
    label: "Research",
    Icon: FlaskConical,
    href: (e) => {
      const slug = (e.payload as { slug?: string }).slug;
      return slug ? `/research/${slug}` : null;
    },
  },
  prompt: { label: "Prompt", Icon: MessageSquareQuote, href: () => "/workspace" },
  jam: {
    label: "Jam",
    Icon: Users,
    href: (e) => {
      const slug = (e.payload as { slug?: string }).slug;
      return slug ? `/jams/${slug}` : null;
    },
  },
  protocol_run: { label: "Run", Icon: Rocket, href: () => null },
  agent_artifact: {
    label: "Artifact",
    Icon: Bot,
    href: (e) => `/library/artifact/${e.id}`,
  },
};

interface Props {
  entry: LibraryEntry;
  isFocused?: boolean;
}

const LibraryEntryRow = ({ entry, isFocused = false }: Props) => {
  const meta = KIND_META[entry.kind];
  const Icon = meta.Icon;
  const href = meta.href(entry);
  const [cloneOpen, setCloneOpen] = useState(false);

  const isAgentArtifact = entry.kind === "agent_artifact";
  const artifactPayload = isAgentArtifact
    ? ((entry.payload as {
        goal?: string;
        brain_model?: string;
        tools?: string[];
        agent_name?: string;
      } | null) ?? {})
    : null;

  const stop = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const inner = (
    <div
      id={`entry-${entry.id}`}
      tabIndex={isFocused ? -1 : undefined}
      className={cn(
        "group relative flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2.5 transition-colors",
        "hover:border-primary/40 hover:bg-card/80",
        entry.is_featured && "border-primary/40 bg-primary/[0.03]",
        isFocused &&
          "border-primary ring-2 ring-primary/30 scroll-mt-24",
      )}
    >
      {/* Kind icon */}
      <div className="shrink-0 w-7 h-7 rounded-md border border-border bg-background flex items-center justify-center text-muted-foreground group-hover:text-primary group-hover:border-primary/40 transition-colors">
        <Icon className="w-3.5 h-3.5" />
      </div>

      {/* Title + meta — single column */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          {entry.is_featured && (
            <Star
              className="w-3 h-3 shrink-0 fill-primary text-primary"
              aria-label="Featured pick"
            />
          )}
          <h3 className="font-mono font-semibold text-sm truncate group-hover:text-primary transition-colors">
            {entry.title}
          </h3>
        </div>
        <div className="mt-0.5 flex items-center gap-2 text-[10px] font-mono text-muted-foreground">
          <span className="uppercase tracking-wider">{meta.label}</span>
          {entry.tags.slice(0, 2).map((t) => (
            <Badge
              key={t}
              variant="secondary"
              className="font-mono text-[9px] px-1.5 py-0 h-4"
            >
              {t}
            </Badge>
          ))}
          <span className="ml-auto sm:ml-0 inline-flex items-center gap-1">
            <GitFork className="w-3 h-3" />
            {entry.fork_count}
          </span>
          <span className="inline-flex items-center gap-1">
            <Eye className="w-3 h-3" />
            {entry.view_count}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="shrink-0 flex items-center gap-1.5" onClick={stop}>
        {isAgentArtifact ? (
          <Button
            type="button"
            size="sm"
            variant="default"
            onClick={(e) => {
              stop(e);
              setCloneOpen(true);
            }}
            className="font-mono h-7 text-[11px] gap-1 px-2"
            title="Clone the goal, model, and tools into a new agent run"
          >
            <Bot className="w-3 h-3" /> Clone
          </Button>
        ) : (
          <ForkToChatButton
            entry={entry}
            variant="default"
            className="h-7 text-[11px] px-2"
          />
        )}
        <ForkLibraryButton entry={entry} className="h-7 text-[11px] px-2" />
      </div>

      {isAgentArtifact && artifactPayload && (
        <div onClick={stop}>
          <CloneArtifactRunDialog
            open={cloneOpen}
            onOpenChange={setCloneOpen}
            payload={artifactPayload}
            artifactTitle={entry.title}
          />
        </div>
      )}
    </div>
  );

  if (!href) return inner;
  return (
    <Link
      to={href}
      onClick={() => bumpLibraryView(entry.id)}
      className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
    >
      {inner}
    </Link>
  );
};

export default LibraryEntryRow;